// store/modules/user.js
import axios from "axios";
import {secureStorage} from "@/services/secureStorage";
import echoService from "@/services/echoService";
import { normalizeTricklistEntries, writeCachedTricklist } from "@/lib/tricklistVersionCache";

const storedToken = secureStorage.getItem('setIn');

export default {
    namespaced: true,
    state: () => ({
        isAuthenticated: Boolean(storedToken),
        token: storedToken || null,
        user: JSON.parse(secureStorage.getItem('profile')) || null,
        tricks: JSON.parse(secureStorage.getItem('tricks')) || null,
        userMode: secureStorage.getItem("userMode") || null,
    }),
    mutations: {
        setAuth(state, payload) {
            state.isAuthenticated = Boolean(payload.isAuthenticated);
            state.token = payload.token; // Agrega el token al estado
            state.user = payload.user; // Agrega el token al estado
            state.tricks = payload.tricks; // Agrega el token al estado
            state.userMode = payload.userMode ?? state.userMode; // 🛠 Solución: No sobrescribe si ya hay un modo
        },
        setUserMut(state, payload) {
            state.user = {
                ...state.user,
                ...payload
            };
        },
        setUserMode(state, mode) {
            state.userMode = mode;
            secureStorage.setItem("userMode", mode);
        },
        setTricks(state, payload) {
            state.tricks = payload?.tricks ?? [];
        },
        logout(state) {
            state.isAuthenticated = false;
            state.token = null;
            state.user = null;
            state.tricks = null;
            state.userMode = null; // 🛠 Solución: Se borra userMode correctamente en Vuex
            localStorage.clear();
            // secureStorage.removeItem('setIn');
            // secureStorage.removeItem('profile');
            // secureStorage.removeItem('tricks');
            // secureStorage.removeItem('userMode'); // Se borra del almacenamiento seguro
        },
    },
    actions: {
        setLogin({ commit, dispatch }, data) {
            if (!data || !data.token || !data.user) {
                console.warn('❌ Datos de login incompletos:', data);
                return false;
            }

            const sanitizedTricks = (data.tricks || []).map(trick => {
                trick.value = parseFloat(trick.value) || 0;
                trick.score = parseFloat(trick.score) || 0;
                return trick;
            });

            secureStorage.setItem('setIn', data.token);
            secureStorage.setItem('profile', JSON.stringify(data.user));
            secureStorage.setItem('tricks', JSON.stringify(sanitizedTricks));

            if (typeof echoService.refreshAuthHeaders === 'function') {
                echoService.refreshAuthHeaders();
            }

            commit('setAuth', {
                isAuthenticated: true,
                token: data.token,
                user: data.user,
                tricks: sanitizedTricks,
                userMode: secureStorage.getItem("userMode") || null
            });

            dispatch('cookieConsent/syncFromUser', data.user, { root: true });

            return true; // ✅ Muy importante para continuar el flujo
        },

        async setLogout({ commit, state, dispatch }) {
            const token = state.token || secureStorage.getItem('setIn');

            try {
                if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function' && typeof CustomEvent === 'function') {
                    const pendingTasks = [];
                    window.dispatchEvent(new CustomEvent('slackline:before-logout', {
                        detail: { pendingTasks },
                    }));

                    if (pendingTasks.length) {
                        await Promise.allSettled(pendingTasks);
                    }
                }

                if (token) {
                    await axios.post('/logout', {}, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            Accept: 'application/json',
                        },
                    });
                } else {
                    await axios.post('/logout');
                }
            } catch (error) {
                console.warn('⚠️ Error al cerrar sesión en el backend:', error?.response || error);
            } finally {
                commit('logout');
                dispatch('cookieConsent/resetConsentState', null, { root: true });
                if (typeof echoService.refreshAuthHeaders === 'function') {
                    echoService.refreshAuthHeaders();
                }
            }
        },
        setUser({ state, commit, dispatch }, updatedUser) {
            const mergedUser = {
                ...state.user,
                ...updatedUser
            };

            commit('setUserMut', updatedUser);
            secureStorage.setItem('profile', JSON.stringify(mergedUser));
            dispatch('cookieConsent/syncFromUser', mergedUser, { root: true });
        },
        setUserMode({ commit }, mode) {
            commit("setUserMode", mode);
        },
        async refreshTricks({ commit, state }, params = {}) {
            const token = state.token || secureStorage.getItem('setIn');
            if (!token) {
                return { tricks: [], tricklistVersionId: null };
            }

            const response = await axios.get('/tricks', {
                params,
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: 'application/json',
                },
            });

            const normalizedTricks = normalizeTricklistEntries(response.data?.tricks || []);
            const tricklistVersionId = response.data?.tricklist_version_id ?? params?.tricklist_version_id ?? null;

            writeCachedTricklist(secureStorage, tricklistVersionId, normalizedTricks);
            commit('setTricks', { tricks: normalizedTricks });

            return {
                tricks: normalizedTricks,
                tricklistVersionId,
            };
        },
    },
    getters: {
        isAuthenticated: state => Boolean(state.isAuthenticated),
        getToken: state => state.token, // Nuevo getter para acceder al token
        getUser: state => state.user, // Nuevo getter para acceder al user
        getRoles: state => state.user ? state.user.roles : [], // Nuevo getter para acceder a los roles
        getUserMode: (state) => state.userMode,
    }
};
